/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Detalles de Tenant - Panel Admin (módulo lazy)
 * @file TenantDetails.jsx
 */

import { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import { useLanguage } from '../../../context/LanguageContext';
import { useModal } from '../../../context/ModalContext';
import { useTheme } from '../../../context/ThemeContext';
import { formatLimit as formatLimitFromPlan, normalizePlanLimits } from '../../../utils/planNormalizer';
import '../AdminDashboard.css';

const TenantDetails = ({ tenantId, onBack }) => {
  const { t } = useLanguage();
  const { showError } = useModal();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const { theme: currentTheme } = useTheme();
  const isDark = currentTheme === 'dark';

  useEffect(() => {
    loadDetails();
  }, [tenantId]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAdminTenantDetails(tenantId);
      if (response.success && response.data) {
        setDetails(response.data);
      } else {
        await showError(
          t('admin.error.title'),
          response.error || t('admin.error.load_details')
        );
        onBack();
      }
    } catch (error) {
      console.error('[TENANT_DETAILS] Error:', error);
      await showError(
        t('admin.error.title'),
        error.message || t('admin.error.generic')
      );
      onBack();
    } finally {
      setLoading(false);
    }
  };

  const formatPlanName = (plan) => {
    const plans = {
      free: t('admin.plan.free'),
      pro: t('admin.plan.pro'),
      enterprise: t('admin.plan.enterprise')
    };
    return plans[plan] || plan.toUpperCase();
  };

  const formatLimit = (value, unit = '') =>
    formatLimitFromPlan(value, { unit: unit ? ` ${unit}` : '', t });

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return '#ef4444';
    if (percentage >= 70) return '#f59e0b';
    return '#10b981';
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>{t('admin.loading')}</p>
      </div>
    );
  }

  if (!details) {
    return null;
  }

  const { tenant, plan, usage, percentages, last_activity, subscription } = details;

  const normalized = normalizePlanLimits({ plan_name: plan?.plan_name, limits: plan?.limits, usage: usage ?? {}, percentages: percentages ?? {} });
  if (!normalized) return null;
  const limits = normalized.limits;
  const usageNorm = normalized.usage;
  const pct = normalized.percentages;

  return (
    <div className="tenant-details-container">
      <div className="tenant-details-header">
        <button className="btn-back" onClick={onBack}>
          ← {t('buttons.back')}
        </button>
        <h2>{tenant.nombre || `Tenant ${tenant.tenant_id}`}</h2>
      </div>

      <div className="tenant-details-grid">
        <div className="detail-card">
          <h3>{t('admin.details.tenant_info')}</h3>
          <div className="detail-item">
            <span className="detail-label">{t('admin.details.tenant_id')}</span>
            <span className="detail-value">{tenant.tenant_id}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">{t('admin.details.name')}</span>
            <span className="detail-value">{tenant.nombre || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">{t('admin.details.domain')}</span>
            <span className="detail-value">{tenant.dominio || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">{t('admin.details.status')}</span>
            <span className={`status-badge ${tenant.activo === 1 ? 'active' : 'suspended'}`}>
              {tenant.activo === 1
                ? (t('admin.status.active'))
                : (t('admin.status.suspended'))}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">{t('admin.details.created')}</span>
            <span className="detail-value">
              {tenant.creado_en ? new Date(tenant.creado_en).toLocaleDateString() : '-'}
            </span>
          </div>
          {last_activity && (
            <div className="detail-item">
              <span className="detail-label">{t('admin.details.last_activity')}</span>
              <span className="detail-value">
                {new Date(last_activity).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        <div className="detail-card">
          <h3>{t('admin.details.plan')}</h3>
          <div className="detail-item">
            <span className="detail-label">{t('admin.details.current_plan')}</span>
            <span className={`plan-badge plan-${plan.plan_name}`}>
              {formatPlanName(plan.plan_name)}
            </span>
          </div>
          <div className="limits-list">
            <div className="limit-item">
              <span>{t('admin.limit.users')}</span>
              <strong>{formatLimit(limits.users)}</strong>
            </div>
            <div className="limit-item">
              <span>{t('admin.limit.messages')}</span>
              <strong>{formatLimit(limits.messages)}</strong>
            </div>
            <div className="limit-item">
              <span>{t('admin.limit.invitations')}</span>
              <strong>{formatLimit(limits.invitations)}</strong>
            </div>
            <div className="limit-item">
              <span>{t('admin.limit.storage')}</span>
              <strong>{formatLimit(limits.storage, 'MB')}</strong>
            </div>
            <div className="limit-item">
              <span>{t('admin.limit.devices')}</span>
              <strong>{formatLimit(limits.devices_per_user)}</strong>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <h3>{t('admin.details.usage')}</h3>
          <div className="usage-metrics">
            <div className="usage-item">
              <div className="usage-header">
                <span>{t('admin.usage.users')}</span>
                <span className="usage-value">
                  {usageNorm.users} / {formatLimit(limits.users)}
                </span>
              </div>
              {(Number(limits.users) || 0) > 0 && (
                <div className="usage-progress">
                  <div
                    className="usage-progress-bar"
                    style={{
                      width: `${Math.min(100, pct.users)}%`,
                      backgroundColor: getProgressColor(pct.users)
                    }}
                  />
                  <span className="usage-percentage">{pct.users.toFixed(1)}%</span>
                </div>
              )}
            </div>

            <div className="usage-item">
              <div className="usage-header">
                <span>{t('admin.usage.messages')}</span>
                <span className="usage-value">
                  {usageNorm.messages} / {formatLimit(limits.messages)}
                </span>
              </div>
              {(Number(limits.messages) || 0) > 0 && (
                <div className="usage-progress">
                  <div
                    className="usage-progress-bar"
                    style={{
                      width: `${Math.min(100, pct.messages)}%`,
                      backgroundColor: getProgressColor(pct.messages)
                    }}
                  />
                  <span className="usage-percentage">{pct.messages.toFixed(1)}%</span>
                </div>
              )}
            </div>

            <div className="usage-item">
              <div className="usage-header">
                <span>{t('admin.usage.invitations')}</span>
                <span className="usage-value">
                  {usageNorm.invitations} / {formatLimit(limits.invitations)}
                </span>
              </div>
              {(Number(limits.invitations) || 0) > 0 && (
                <div className="usage-progress">
                  <div
                    className="usage-progress-bar"
                    style={{
                      width: `${Math.min(100, pct.invitations)}%`,
                      backgroundColor: getProgressColor(pct.invitations)
                    }}
                  />
                  <span className="usage-percentage">{pct.invitations.toFixed(1)}%</span>
                </div>
              )}
            </div>

            <div className="usage-item">
              <div className="usage-header">
                <span>{t('admin.usage.storage')}</span>
                <span className="usage-value">
                  {usageNorm.storage.toFixed(2)} MB / {formatLimit(limits.storage, 'MB')}
                </span>
              </div>
              {(Number(limits.storage) || 0) > 0 && (
                <div className="usage-progress">
                  <div
                    className="usage-progress-bar"
                    style={{
                      width: `${Math.min(100, pct.storage)}%`,
                      backgroundColor: getProgressColor(pct.storage)
                    }}
                  />
                  <span className="usage-percentage">{pct.storage.toFixed(1)}%</span>
                </div>
              )}
            </div>

            <div className="usage-item">
              <div className="usage-header">
                <span>{t('admin.usage.devices')}</span>
                <span className="usage-value">{usageNorm.devices_active}</span>
              </div>
            </div>
          </div>
        </div>

        {subscription && (
          <div className="detail-card">
            <h3>{t('admin.details.subscription')}</h3>
            <div className="detail-item">
              <span className="detail-label">{t('admin.subscription.id')}</span>
              <span className="detail-value">{subscription.mp_subscription_id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('admin.subscription.status')}</span>
              <span className="detail-value">{subscription.mp_status || '-'}</span>
            </div>
            {subscription.mp_last_payment && (
              <div className="detail-item">
                <span className="detail-label">{t('admin.subscription.last_payment')}</span>
                <span className="detail-value">
                  {new Date(subscription.mp_last_payment).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantDetails;
